import { useCallback, useMemo } from 'react'

import { getServerLikesActive } from '@wwt/core/admin'
import { TutorialStatus, useTutorialStore } from '@wwt/core/tutorial'
import { ReasonType } from '@wwt/entities/reason'
import { Offer } from '@wwt/entities/search-response'
import {
	ReasonPopup,
	ReasonPopupProps
} from '@wwt/widgets/popup/like-dislike-reason/ui/ReasonPopup.tsx'

import { saveOfferQuery } from '@/entities/offer'
import { useToast } from '@/shared/lib/hooks/useToast.tsx'

import { wwtReasonLog } from './logger/wwtReasonLog.ts'
import { useReasonInfo } from './modals/useReasonInfo.ts'
import { useReasonReview } from './modals/useReasonReview.ts'
import { useCoreOfferReasonPopupStore } from './model/useCoreOfferReasonPopupStore.ts'

const getReasonPopupId = () =>
	useCoreOfferReasonPopupStore.getState().reasonPopupId

export const useOfferReasonPopup = (
	// TODO: Yes, it's bad. Any ideas on how to improve it?
	onDislikeCancel?: () => void
) => {
	const tutorialStatus = useTutorialStore.use.tutorialStatus()

	const { toast, dismiss } = useToast()

	const setReasonPopupId = useCoreOfferReasonPopupStore.use.setReasonPopupId()

	const addPopupToStacked = useCallback(
		(props: ReasonPopupProps, callback: (id: string) => void) => {
			callback(
				toast({
					className: 'p-0',
					custom: <ReasonPopup {...props} />
				}).id
			)
		},
		[]
	)

	const { onOpenReview } = useReasonReview()
	const { onOpenInfo } = useReasonInfo()

	const showPopup = useCallback(
		(offer: Offer, type: ReasonType) => {
			wwtReasonLog(`Show ${type} popup`)

			const reasonPopupId = getReasonPopupId()

			if (reasonPopupId) {
				wwtReasonLog('Popup already opened, remove previous popup')
				dismiss(reasonPopupId)
				setReasonPopupId(null)
			}

			const onClose = () => {
				const reasonPopupId = getReasonPopupId()
				if (reasonPopupId) dismiss(reasonPopupId)
				setReasonPopupId(null)
			}

			const closeStackedReason = () => {
				const reasonPopupId = getReasonPopupId()
				if (reasonPopupId === null) {
					throw new Error('Try to close stacked reason with id = null')
				}

				wwtReasonLog('Close stacked reason')

				dismiss(reasonPopupId)
				onClose()
			}

			const baseProps: ReasonPopupProps = {
				type: type,
				isShowed: tutorialStatus !== TutorialStatus.GOING,
				onClose,
				offer,
				offerId: '1aaaaaaa-bbbb-bbbb-bbbb-bbbbbbbbbbbb',
				onOpenReview: (offerId: string) => {
					wwtReasonLog('Try to open review modal')

					onOpenReview(closeStackedReason, offerId, type)
				},
				onOpenInfo: () => {
					wwtReasonLog('Try to open info modal')

					onOpenInfo()
				},
				onDislikeCancel
			}

			if (!getServerLikesActive()) {
				wwtReasonLog('Server likes disabled')
				wwtReasonLog(`Show ${type} popup without save offer`)

				addPopupToStacked(baseProps, setReasonPopupId)
				return
			}

			wwtReasonLog('Server likes enabled')

			if (offer.IDs.wwtOfferId !== undefined) {
				wwtReasonLog(
					'wwtOfferId in offer is not undefined',
					offer.IDs.wwtOfferId
				)

				baseProps.offerId = offer.IDs.wwtOfferId

				addPopupToStacked(baseProps, setReasonPopupId)
				return
			}

			wwtReasonLog('wwtOfferId in offer is undefined')
			wwtReasonLog('Save offer request')

			saveOfferQuery(offer).then(({ wwtOfferId }) => {
				wwtReasonLog(`Show ${type} reason popup with offerId = `, wwtOfferId)

				baseProps.offerId = wwtOfferId

				addPopupToStacked(baseProps, setReasonPopupId)
			})
		},
		[tutorialStatus, addPopupToStacked, setReasonPopupId, onDislikeCancel]
	)

	const showDislikePopup = useCallback(
		(offer: Offer) => {
			showPopup(offer, ReasonType.dislike)
		},
		[showPopup]
	)

	const showLikePopup = useCallback(
		(offer: Offer) => {
			showPopup(offer, ReasonType.like)
		},
		[showPopup]
	)

	return useMemo(
		() => ({ showDislikePopup, showLikePopup }),
		[showDislikePopup, showLikePopup]
	)
}
